/**
 * The retainer — `mockups/studio/client-detail.html` §Retainer.
 *
 * The mockup's own comment: "this page owns the block-by-block build-up. What the
 * retainer costs and what its allowances absorbed are two views of the same retainer,
 * so they sit in ONE region, side by side, with no border between them (P8)."
 *
 * **Every row is a stored field.** The block rows are `retainer.items` as they
 * were snapshotted at signup — `kind: 'catalog'` for a block of the card and
 * `kind: 'custom'` for a line agreed with this client alone, which `CustomItemRow`
 * draws — and the four under them are
 * `retainer.pricing_breakdown`'s own steps in its own order — the same
 * `MonthlyBreakdownView` `Breakdown` prints for a *computed* price. This one is not
 * computed, which is why it is not `Breakdown`: the mockup interleaves the item rows
 * and the build-up rows in one table, and `Breakdown` owns its own. That is the third
 * renderer of `MonthlyBreakdownView` in the SPA and the report says where it should
 * be extracted to.
 *
 * The allowances are session 19's `AllowanceMeters`, which is P3 as a component —
 * meters for the ones over, a fold for the rest — rather than the mockup's own
 * "other five allowances" table.
 */

import {
  isZero,
  toMajorUnits,
  tierMultiplierOf,
  type Currency,
  type MonthlyBreakdownView,
  type RetainerItemView,
  type StudioClientDetailView,
  type TierCode,
  isTiered,
} from '@fareast/shared'

import { AllowanceMeters } from '../../../components/client/index.js'
import {
  CUSTOM_LINES_NOTE,
  CustomBreakdownRows,
  CustomItemRow,
  catalogItemsOf,
  isCatalogItem,
  retainerItemKey,
} from '../../../components/client/customLines.js'
import { Empty, Fold } from '../../../components/portal/Disclosure.js'
import {
  Push,
  Row,
  ScrollX,
  Section,
  SectionHead,
  Stack,
} from '../../../components/portal/Layout.js'
import { Meter } from '../../../components/portal/Meter.js'
import { Region, RegionCols, RegionHead } from '../../../components/portal/Region.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import { Answer, DayText, Muted, Num, Prose, Small } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'
import { EditRetainerDialog } from './EditRetainerDialog.js'

export const RETAINER_ID = 'retainer'

export type RetainerSectionProps = {
  readonly detail: StudioClientDetailView
  /**
   * `account.tier` and `account.currency`, for the package the dialog **proposes**.
   *
   * The account's, not `retainer.market_tier` — the snapshot beside it is what the
   * *current* package was priced at, and a change is priced at what the client is on
   * today. `ClientDetailPage` parses both; they arrive here as the closed unions
   * `catalog_compute_price` takes.
   */
  readonly tier: TierCode
  readonly currency: Currency
}

export function RetainerSection({ detail, tier, currency }: RetainerSectionProps) {
  const format = useFormat()
  const { retainer, allowances, ad_spend: adSpend } = detail

  if (retainer === null) {
    return (
      <Section id={RETAINER_ID}>
        <SectionHead title="Retainer" />
        <Empty>
          No live retainer. A cancelled one keeps its invoices and its files, and nothing here can
          be edited.
        </Empty>
      </Section>
    )
  }

  const over = allowances.filter((one) => one.over > 0)
  /*
   * **The head counts blocks, and a custom line is not one.** `retainer.items` is
   * one list of two kinds since custom lines (`PRICING.md` §Custom lines), and the
   * bundle discount this head is about is computed over the blocks alone — so
   * "9 blocks" on a package of seven blocks and two agreed lines would be the one
   * number on this page that disagrees with the engine.
   */
  const blocks = catalogItemsOf(retainer.items)
  const customCount = retainer.items.length - blocks.length

  return (
    <Section id={RETAINER_ID}>
      <SectionHead
        title="Retainer"
        note={`Snapshotted ${format.day(retainer.started_on)} — rate-card changes since then do not touch it`}
      />

      <Region>
        <RegionHead
          level={3}
          title={`${retainer.name} — ${format.qty(blocks.length, {
            one: 'block',
            other: 'blocks',
          })}${
            customCount === 0
              ? ''
              : ` and ${format.qty(customCount, { one: 'custom line', other: 'custom lines' })}`
          }`}
        />
        <Row>
          <Push />
          <EditRetainerDialog
            client={detail.client}
            retainer={retainer}
            tier={tier}
            currency={currency}
          />
        </Row>

        <Answer>
          {over.length === 0
            ? 'Every allowance is inside what the package includes this month.'
            : `${format.number(over.length)} of ${format.number(allowances.length)} allowances went past what the package includes this month.`}
        </Answer>

        <RegionCols>
          <Stack>
            <ScrollX>
              <Table>
                <THead>
                  <Tr>
                    <Th scope="col">Service</Th>
                    <Th scope="col" right>
                      Blocks
                    </Th>
                    <Th scope="col">Allowance</Th>
                    <Th scope="col" right>
                      Amount
                    </Th>
                  </Tr>
                </THead>
                <TBody>
                  {retainer.items.map((item) =>
                    isCatalogItem(item) ? (
                      <ItemRow key={retainerItemKey(item)} item={item} />
                    ) : (
                      <CustomItemRow key={retainerItemKey(item)} item={item} />
                    ),
                  )}
                  {retainer.pricing_breakdown === null ? null : (
                    <BuildUpRows breakdown={retainer.pricing_breakdown} />
                  )}
                </TBody>
              </Table>
            </ScrollX>

            <Fold summary="How this price is computed, and how it is holding up">
              {retainer.pricing_breakdown === null ? null : (
                <Prose>
                  Every block is computed, never entered by hand:{' '}
                  {format.qty(retainer.pricing_breakdown.block_count, {
                    one: 'block',
                    other: 'blocks',
                  })}{' '}
                  at list, less the {format.percent(retainer.pricing_breakdown.bundle_discount_bps)}{' '}
                  bundle discount, times the market tier {format.number(retainer.market_tier)}{' '}
                  multiplier, rounded to the nearest ten.
                </Prose>
              )}
              {/*
                The one sentence on this page that would otherwise be false.
                "Every line is computed" is what the fold has said since session 20,
                and a custom line is the exact opposite of that — a figure agreed with
                this client and charged as given. It is said here rather than left to
                the row's own label because this is the paragraph a reader opens to
                find out where the number came from.
              */}
              {customCount === 0 ? null : (
                <Prose>{`${CUSTOM_LINES_NOTE} They are added to the blocks, and the total is the two together.`}</Prose>
              )}
              <Prose>
                {over.length === 0
                  ? 'Nothing went past an allowance this month, so nothing was billed as an extra.'
                  : `${format.number(over.length)} of ${format.number(
                      allowances.length,
                    )} allowances were exceeded. That is the model working — the overage is approved and billed as an extra, and the extras ledger above is what it comes to.`}
              </Prose>
              <Prose>
                The snapshot is what this retainer is priced on. It was taken on{' '}
                <DayText value={retainer.started_on} nowrap /> and a later change to the rate card
                does not touch it; a change to the package itself is proposed with{' '}
                <b>Edit retainer</b> and applies on a billing day.
              </Prose>
            </Fold>
          </Stack>

          <Stack>
            {adSpend === null ? null : (
              <Meter
                label="Ad spend band"
                used={toMajorUnits(adSpend.spent)}
                included={toMajorUnits(adSpend.band)}
                foot={
                  <>
                    <Num value={adSpend.spent} /> of <Num value={adSpend.band} /> · {adSpend.note}
                  </>
                }
              />
            )}

            <AllowanceMeters
              allowances={allowances}
              emptyLine="Every allowance is inside what the package includes this month."
            />
          </Stack>
        </RegionCols>
      </Region>
    </Section>
  )
}

/* ------------------------------------------------------------------ one block */

function ItemRow({ item }: { readonly item: RetainerItemView }) {
  const format = useFormat()

  return (
    <Tr>
      <Td>{item.service_name}</Td>
      <Td right num>
        <Num value={item.blocks} />
      </Td>
      <Td>
        <Small>
          <Muted>
            {item.allowance_unit === null || item.allowance_qty === null
              ? 'block scope'
              : format.qty(item.allowance_qty, {
                  one: item.allowance_unit,
                  other: `${item.allowance_unit}s`,
                })}
          </Muted>
        </Small>
      </Td>
      <Td right num>
        <Num value={item.amount} />
      </Td>
    </Tr>
  )
}

/* --------------------------------------------------------------- the build-up */

/**
 * `MonthlyBreakdownView`'s own steps, in its own order.
 *
 * The tier row and the conversion row are **running totals, not deltas**, and each is
 * printed only when it says something: a Tier 1 client's multiplier is ×1.0 and a USD
 * client is not converted, so Casaturca prints neither and a Tier 2 EUR client prints
 * both. The rounding line is shown only when it is non-zero
 * (`.docs/00-index.md` §6), which is what makes the column add up: the engine's
 * discount is the exact −$561.00 and the mockup's −$560.00 is that plus the +$1.00
 * rounding, shown as two lines rather than one wrong one.
 */
function BuildUpRows({ breakdown }: { readonly breakdown: MonthlyBreakdownView }) {
  const format = useFormat()
  const tiered = isTiered(breakdown.tier)

  return (
    <>
      <Tr variant="sub">
        <Td>Blocks at list</Td>
        <Td right num>
          <Num value={breakdown.block_count} />
        </Td>
        <Td />
        <Td right num>
          <Num value={breakdown.list} />
        </Td>
      </Tr>

      <Tr>
        <Td>
          Bundle discount — {format.qty(breakdown.block_count, { one: 'block', other: 'blocks' })}
        </Td>
        <Td />
        <Td>
          <Small>
            <Muted>{format.percent(breakdown.bundle_discount_bps)}</Muted>
          </Small>
        </Td>
        <Td right num>
          <Num value={breakdown.bundle_discount} />
        </Td>
      </Tr>

      <Tr variant={tiered ? 'sub' : undefined}>
        <Td>Market tier {format.number(breakdown.tier.code)}</Td>
        <Td />
        <Td>
          <Small>
            <Muted>
              ×
              {format.number(tierMultiplierOf(breakdown.tier.multiplier_bps), {
                fractionDigits: 1,
              })}
            </Muted>
          </Small>
        </Td>
        <Td right num>
          {tiered ? <Num value={breakdown.tier.amount} /> : <Muted>—</Muted>}
        </Td>
      </Tr>

      {breakdown.converted === null ? null : (
        <Tr variant="sub">
          <Td colSpan={3}>Converted to {breakdown.currency}</Td>
          <Td right num>
            <Num value={breakdown.converted} />
          </Td>
        </Tr>
      )}

      {isZero(breakdown.rounding) ? null : (
        <Tr>
          <Td colSpan={3}>Rounded to the nearest $10</Td>
          <Td right num>
            <Num value={breakdown.rounding} />
          </Td>
        </Tr>
      )}

      {/*
       * The blocks subtotal, each agreed line and their total — or nothing at all,
       * which is what keeps a card-only package printing exactly what it did before.
       * `CustomBreakdownRows` is the one copy of it; this table is four columns.
       */}
      <CustomBreakdownRows breakdown={breakdown} labelSpan={3} />

      <Tr variant="total">
        <Td colSpan={3}>Monthly, round10</Td>
        <Td right num>
          <Num value={breakdown.total} lg />
        </Td>
      </Tr>
    </>
  )
}
